import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import SearchableSelect from '../../components/common/SearchableSelect';
import { useSortable } from '../../hooks/useSortable';
import { usePaginacao } from '../../hooks/usePaginacao';
import { formatDateTime } from '../../utils/date';
import { useAuth } from '../../contexts/AuthContext';
import * as api from '../../api/estoque';
import * as modelosApi from '../../api/modelos';
import * as coresApi from '../../api/cores';
import * as tamanhosApi from '../../api/tamanhos';
import * as empresasApi from '../../api/empresas';

/* Ícone de chevron que roda 90° quando expandido */
function Chevron({ aberto }) {
  return (
    <svg
      viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
      style={{
        width: 14, height: 14,
        transition: 'transform .18s ease',
        transform: aberto ? 'rotate(90deg)' : 'rotate(0deg)',
      }}
    >
      <polyline points="5 3 11 8 5 13" />
    </svg>
  );
}

const LINHA_VAZIA = () => ({ id: Math.random(), id_tamanho_uniforme: '', id_cor: '', quantidade: '' });

function ModalLancamento({ modelos, cores, tamanhosCatalogo, empresas, emGlobal, initial, onClose, onSaved }) {
  // Prefill vindo da tela de Demandas (atalho "Ir para Estoque" ao concluir produção)
  const modeloInicial = initial?.idModelo ? modelos.find(m => m.id_modelo_uniforme === initial.idModelo) : null;

  const tamanhoNome = id => tamanhosCatalogo.find(t => t.id_tamanho_uniforme === id)?.nome || id;

  const [tipo,         setTipo]        = useState('entrada'); // "entrada" | "saida"
  const [idModelo,    setIdModelo]    = useState(initial?.idModelo || '');
  const [tamanhosDoModelo, setTamanhosDoModelo] = useState(modeloInicial?.tamanhos?.map(t => t.id_tamanho_uniforme) || []);
  const [coresModelo, setCoresModelo] = useState(
    modeloInicial
      ? (modeloInicial.cores || []).map(mc => cores.find(c => c.id_cor === mc.id_cor)).filter(Boolean)
      : []
  ); // [{id_cor, nome, codigo_hex}]
  const [precisaCor,  setPrecisaCor]  = useState(!!modeloInicial?.permite_variacao_cor);
  const [observacao,  setObservacao]  = useState('');
  const [linhas,      setLinhas]      = useState([
    initial?.id_tamanho_uniforme
      ? { id: Math.random(), id_tamanho_uniforme: initial.id_tamanho_uniforme, id_cor: initial.idCor || '', quantidade: initial.quantidade ?? '' }
      : LINHA_VAZIA(),
  ]);
  const [loading,     setLoading]     = useState(false);
  const [erro,        setErro]        = useState('');

  const selecionarModelo = (id) => {
    const m = modelos.find(x => x.id_modelo_uniforme === id);
    setTamanhosDoModelo(m?.tamanhos?.map(t => t.id_tamanho_uniforme) || []);
    setPrecisaCor(!!m?.permite_variacao_cor);
    setCoresModelo(
      (m?.cores || [])
        .map(mc => cores.find(c => c.id_cor === mc.id_cor))
        .filter(Boolean)
    );
    setIdModelo(id);
    setLinhas([LINHA_VAZIA()]);
  };

  const setLinha = (id, campo, valor) =>
    setLinhas(ls => ls.map(l => l.id === id ? { ...l, [campo]: valor } : l));

  const addLinha = () => setLinhas(ls => [...ls, LINHA_VAZIA()]);

  const removeLinha = (id) => setLinhas(ls => ls.filter(l => l.id !== id));

  const salvar = async (e) => {
    e.preventDefault();
    setErro('');

    const validas = linhas.filter(l => l.id_tamanho_uniforme && parseInt(l.quantidade) > 0 && (!precisaCor || l.id_cor));
    if (validas.length === 0) {
      setErro(precisaCor
        ? 'Adicione ao menos uma linha com tamanho, cor e quantidade válida.'
        : 'Adicione ao menos um tamanho com quantidade válida.');
      return;
    }

    const chaves = validas.map(l => `${l.id_tamanho_uniforme}|${l.id_cor || ''}`);
    if (new Set(chaves).size !== chaves.length) {
      setErro('Há tamanho+cor repetidos entre as linhas.');
      return;
    }

    setLoading(true);
    try {
      await Promise.all(validas.map(l =>
        api.lancarEntrada({
          id_modelo_uniforme: idModelo,
          id_tamanho_uniforme: l.id_tamanho_uniforme,
          id_cor:     precisaCor ? l.id_cor : null,
          tipo,
          quantidade: parseInt(l.quantidade),
          observacao: observacao || undefined,
        })
      ));
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      setErro(typeof d === 'string' ? d : 'Erro ao lançar. Tente novamente.');
    } finally { setLoading(false); }
  };

  // No Modo Global, /estoque/ e /modelos-uniforme/ vêm sem filtro de empresa — sem o
  // nome da empresa junto, ficaria impossível distinguir modelos homônimos (ou só
  // "de qual empresa é esse?") de empresas diferentes na hora de lançar movimento.
  const empresaNomeDoModelo = id => empresas.find(e => e.id_empresa_operadora === id)?.nome || '—';
  const modeloOpts = modelos.map(m => ({
    value: m.id_modelo_uniforme,
    label: emGlobal ? `${m.nome} — ${empresaNomeDoModelo(m.id_empresa_operadora)}` : m.nome,
  }));
  const podeContinuar = idModelo && tamanhosDoModelo.length > 0;
  const todasPreenchidas = linhas.every(l => l.id_tamanho_uniforme && parseInt(l.quantidade) > 0 && (!precisaCor || l.id_cor));

  return (
    <div className="modal show d-block" style={{ background: 'rgba(0,0,0,.45)' }}>
      <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: 520 }}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title mb-0">Lançar Movimento de Estoque</h5>
            <button className="btn-close" onClick={onClose} />
          </div>

          <form onSubmit={salvar}>
            <div className="modal-body">
              {erro && <div className="alert alert-danger py-2 mb-3">{erro}</div>}

              {initial?.origem === 'demanda' && (
                <div className="alert alert-info py-2 mb-3" style={{ fontSize: 13 }}>
                  Lançamento sugerido a partir de uma demanda em produção. A quantidade
                  já vem preenchida com o pendente — ajuste se produziu mais ou menos.
                </div>
              )}

              {/* Tipo: entrada ou saída */}
              <div className="mb-3">
                <div className="d-flex gap-2">
                  <button type="button" className={`btn btn-sm flex-fill ${tipo === 'entrada' ? 'btn-success' : 'btn-outline-success'}`}
                    onClick={() => setTipo('entrada')}>+ Entrada</button>
                  <button type="button" className={`btn btn-sm flex-fill ${tipo === 'saida' ? 'btn-danger' : 'btn-outline-danger'}`}
                    onClick={() => setTipo('saida')}>− Saída</button>
                </div>
                {tipo === 'saida' && (
                  <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                    Baixa manual (avaria, perda, venda externa...) — nunca afeta demandas de fabricação.
                  </div>
                )}
              </div>

              {/* Modelo */}
              <div className="mb-3">
                <label className="form-label fw-semibold">Modelo *</label>
                <SearchableSelect options={modeloOpts} value={idModelo}
                  onChange={v => selecionarModelo(v)}
                  placeholder="Selecione o modelo..." required />
              </div>

              {/* Linhas de tamanho + quantidade */}
              {podeContinuar && (
                <>
                  <div className="d-flex align-items-center mb-2">
                    <span className="fw-semibold" style={{ fontSize: 13 }}>Tamanhos *</span>
                  </div>

                  <div className="d-flex flex-column gap-2 mb-2">
                    {linhas.map((linha, idx) => (
                      <div key={linha.id} className="d-flex gap-2 align-items-center">
                        {/* Tamanho */}
                        <select
                          className="form-select form-select-sm"
                          style={{ flex: '0 0 100px' }}
                          value={linha.id_tamanho_uniforme}
                          onChange={e => setLinha(linha.id, 'id_tamanho_uniforme', e.target.value)}
                          required
                        >
                          <option value="">Tam...</option>
                          {tamanhosDoModelo.map(t => (
                            <option key={t} value={t}>{tamanhoNome(t)}</option>
                          ))}
                        </select>

                        {/* Cor — só quando o modelo tem variação de cor */}
                        {precisaCor && (
                          <select
                            className="form-select form-select-sm"
                            style={{ flex: '0 0 130px' }}
                            value={linha.id_cor}
                            onChange={e => setLinha(linha.id, 'id_cor', e.target.value)}
                            required
                          >
                            <option value="">Cor...</option>
                            {coresModelo.map(c => (
                              <option key={c.id_cor} value={c.id_cor}>{c.nome}</option>
                            ))}
                          </select>
                        )}

                        {/* Quantidade */}
                        <input
                          type="number"
                          min="1"
                          className="form-control form-control-sm"
                          placeholder="Qtd."
                          value={linha.quantidade}
                          onChange={e => setLinha(linha.id, 'quantidade', e.target.value)}
                          required
                        />

                        {/* Remover (só mostra se há mais de 1 linha) */}
                        {linhas.length > 1 && (
                          <button type="button" className="btn btn-sm btn-outline-danger px-2 py-1"
                            onClick={() => removeLinha(linha.id)} title="Remover">
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {todasPreenchidas && (
                    <button type="button" className="btn btn-outline-secondary btn-sm mb-3"
                      onClick={addLinha}>
                      + Adicionar linha
                    </button>
                  )}
                </>
              )}

              {/* Observação */}
              <div>
                <label className="form-label fw-semibold">
                  {tipo === 'saida' ? 'Motivo *' : 'Observação'}
                </label>
                <input className="form-control" value={observacao}
                  onChange={e => setObservacao(e.target.value)}
                  required={tipo === 'saida'}
                  placeholder={tipo === 'saida' ? 'Ex: avaria, perda, venda externa...' : 'Ex: NF 12345, lote março...'} />
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary btn-sm" onClick={onClose}>Cancelar</button>
              <button type="submit" className={`btn btn-sm ${tipo === 'saida' ? 'btn-danger' : 'btn-primary'}`} disabled={loading || !podeContinuar}>
                {loading ? <span className="spinner-border spinner-border-sm me-1" /> : null}
                {`Lançar ${tipo === 'saida' ? 'Saída' : 'Entrada'}${linhas.length > 1 ? ` (${linhas.filter(l => l.id_tamanho_uniforme && l.quantidade).length})` : ''}`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function Estoque() {
  const { user } = useAuth();
  const souAdmin = user?.perfil === 'admin';
  const emGlobal = souAdmin && !user?.empresa_ativa_id;

  const [searchParams, setSearchParams] = useSearchParams();
  const [estoque,     setEstoque]     = useState([]);
  const [lancamentos, setLancamentos] = useState([]);
  const [modelos,     setModelos]     = useState([]);
  const [cores,       setCores]       = useState([]);
  const [tamanhos,    setTamanhos]    = useState([]);
  const [empresas,    setEmpresas]    = useState([]);
  const [idEmpresaFiltro, setIdEmpresaFiltro] = useState('');
  const [loading,     setLoading]     = useState(true);
  const [aba,         setAba]         = useState('estoque');
  const [busca,       setBusca]       = useState('');
  const [modal,       setModal]       = useState(false);
  const [prefill,     setPrefill]     = useState(null);
  const [expandidos,  setExpandidos]  = useState(new Set());
  const [erroCarregar, setErroCarregar] = useState('');

  const carregar = useCallback(() => {
    setLoading(true);
    setErroCarregar('');
    Promise.all([api.listar(), api.listarLancamentos(), modelosApi.listar(), coresApi.listar(), tamanhosApi.listar()])
      .then(([e, l, m, c, tam]) => { setEstoque(e.data); setLancamentos(l.data); setModelos(m.data); setCores(c.data); setTamanhos(tam.data); })
      .catch(() => setErroCarregar('Erro ao carregar dados. Recarregue a página.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  useEffect(() => {
    if (souAdmin) empresasApi.listEmpresas().then(r => setEmpresas(r.data)).catch(() => {});
  }, [souAdmin]);

  // Atalho vindo da tela de Demandas ("Concluir Produção" → "Ir para Estoque"):
  // ?lancar=1&modelo=...&tamanho=...&cor=...&qtd=... abre o modal já preenchido.
  useEffect(() => {
    if (loading || modelos.length === 0) return;
    if (searchParams.get('lancar') !== '1') return;
    setPrefill({
      idModelo: searchParams.get('modelo') || '',
      id_tamanho_uniforme: searchParams.get('id_tamanho_uniforme') || '',
      idCor: searchParams.get('cor') || '',
      quantidade: searchParams.get('qtd') || '',
      origem: 'demanda',
    });
    setModal(true);
    setSearchParams({}, { replace: true });
  }, [loading, modelos, searchParams, setSearchParams]);

  const fecharModal = () => { setModal(false); setPrefill(null); };

  /* Mapa rápido id → modelo (nome + empresa) */
  const modeloMap = useMemo(() => {
    const map = {};
    modelos.forEach(m => { map[m.id_modelo_uniforme] = m; });
    return map;
  }, [modelos]);
  const modeloNome = id => modeloMap[id]?.nome || id;
  const corNome = id => cores.find(c => c.id_cor === id)?.nome;
  const tamanhoNome = id => tamanhos.find(t => t.id_tamanho_uniforme === id)?.nome || id;
  const empresaNome = id => empresas.find(e => e.id_empresa_operadora === id)?.nome || '—';

  /* ── Aba Posição Atual — agrupada por modelo ── */
  const grupos = useMemo(() => {
    const byModel = {};
    estoque.forEach(e => {
      if (!byModel[e.id_modelo_uniforme]) {
        byModel[e.id_modelo_uniforme] = {
          id:    e.id_modelo_uniforme,
          nome:  modeloMap[e.id_modelo_uniforme]?.nome || e.id_modelo_uniforme,
          idEmpresa: modeloMap[e.id_modelo_uniforme]?.id_empresa_operadora,
          itens: [],
        };
      }
      byModel[e.id_modelo_uniforme].itens.push(e);
    });

    const q = busca.toLowerCase().trim();
    return Object.values(byModel)
      .filter(g => !emGlobal || !idEmpresaFiltro || g.idEmpresa === idEmpresaFiltro)
      .filter(g =>
        !q ||
        g.nome.toLowerCase().includes(q) ||
        g.itens.some(i => tamanhoNome(i.id_tamanho_uniforme).toLowerCase().includes(q))
      )
      .sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
  }, [estoque, busca, modeloMap, tamanhos, emGlobal, idEmpresaFiltro]);

  const toggleExpandir = (id) => setExpandidos(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const expandirTodos  = () => setExpandidos(new Set(grupos.map(g => g.id)));
  const recolherTodos  = () => setExpandidos(new Set());

  /* Paginação: 10 grupos por página */
  const { paginados: gruposPag, Paginacao: PagEst } = usePaginacao(grupos, 10);

  /* ── Aba Histórico ── */
  const filtradosLanc = lancamentos.filter(l => {
    if (emGlobal && idEmpresaFiltro && modeloMap[l.id_modelo_uniforme]?.id_empresa_operadora !== idEmpresaFiltro) return false;
    const nome = modeloNome(l.id_modelo_uniforme).toLowerCase();
    const q    = busca.toLowerCase();
    return nome.includes(q) || tamanhoNome(l.id_tamanho_uniforme).toLowerCase().includes(q);
  });
  const { sorted: sortedLanc, Th: ThLanc } = useSortable(filtradosLanc, 'created_at');
  const { paginados: pagLanc,  Paginacao: PagLanc } = usePaginacao(sortedLanc, 10);

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      <PageHeader titulo="Estoque" subtitulo={`${grupos.length} produto(s)`}>
        <button className="btn btn-primary btn-sm" onClick={() => setModal(true)}>+ Lançar Movimento</button>
      </PageHeader>

      {erroCarregar && <div className="alert alert-danger py-2">{erroCarregar}</div>}

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link${aba === 'estoque' ? ' active' : ''}`} onClick={() => setAba('estoque')}>
            Posição Atual
          </button>
        </li>
        <li className="nav-item">
          <button className={`nav-link${aba === 'lancamentos' ? ' active' : ''}`} onClick={() => setAba('lancamentos')}>
            Histórico de Movimentos
          </button>
        </li>
      </ul>

      <div className="d-flex gap-2 mb-3 align-items-center flex-wrap">
        <input className="form-control" style={{ maxWidth: 360 }}
          placeholder="Buscar por modelo ou tamanho..."
          value={busca} onChange={e => setBusca(e.target.value)} />

        {emGlobal && (
          <select className="form-select" style={{ maxWidth: 220 }}
            value={idEmpresaFiltro} onChange={e => setIdEmpresaFiltro(e.target.value)}>
            <option value="">Todas as empresas</option>
            {empresas.map(e => <option key={e.id_empresa_operadora} value={e.id_empresa_operadora}>{e.nome}</option>)}
          </select>
        )}

        {aba === 'estoque' && grupos.length > 0 && (
          <div className="d-flex gap-1 ms-auto">
            <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 12 }}
              onClick={expandirTodos}>Expandir todos</button>
            <button className="btn btn-outline-secondary btn-sm" style={{ fontSize: 12 }}
              onClick={recolherTodos}>Recolher todos</button>
          </div>
        )}
      </div>

      {/* ── ABA: POSIÇÃO ATUAL (hierarquia por produto) ── */}
      {aba === 'estoque' && (
        <>
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table mb-0" style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 36 }} />
                    <col />
                    {emGlobal && !idEmpresaFiltro && <col style={{ width: 140 }} />}
                    <col style={{ width: 130 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 130 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th />
                      <th>Produto</th>
                      {emGlobal && !idEmpresaFiltro && <th>Empresa</th>}
                      <th className="text-center">Tamanhos</th>
                      <th className="text-end">Disponível</th>
                      <th className="text-end">Em Fabricação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gruposPag.length === 0 && (
                      <tr>
                        <td colSpan={emGlobal && !idEmpresaFiltro ? 6 : 5} className="text-center text-muted py-4">
                          Nenhum produto em estoque.
                        </td>
                      </tr>
                    )}

                    {gruposPag.map(grupo => {
                      const aberto    = expandidos.has(grupo.id);
                      const totalDisp = grupo.itens.reduce((s, i) => s + i.quantidade_disponivel, 0);
                      const totalFab  = grupo.itens.reduce((s, i) => s + i.quantidade_reservada_fabricacao, 0);
                      const zeroPct   = grupo.itens.filter(i => i.quantidade_disponivel === 0).length;
                      const dispColor = totalDisp === 0 ? 'text-danger fw-bold'
                                      : zeroPct  > 0  ? 'text-warning fw-semibold'
                                      : 'text-success fw-bold';

                      return (
                        <React.Fragment key={grupo.id}>

                          {/* ── Linha do produto ── */}
                          <tr
                            onClick={() => toggleExpandir(grupo.id)}
                            style={{ cursor: 'pointer', userSelect: 'none' }}
                            className={aberto ? 'table-light' : ''}
                          >
                            {/* Ícone expand */}
                            <td className="text-muted" style={{ paddingLeft: 12, verticalAlign: 'middle' }}>
                              <Chevron aberto={aberto} />
                            </td>

                            {/* Nome */}
                            <td style={{ verticalAlign: 'middle' }}>
                              <span className="fw-semibold" style={{ color: 'var(--cjc-primary)' }}>
                                {grupo.nome}
                              </span>
                              {zeroPct > 0 && (
                                <span className="badge bg-danger ms-2" style={{ fontSize: 10, verticalAlign: 'middle' }}>
                                  {zeroPct} sem estoque
                                </span>
                              )}
                            </td>

                            {emGlobal && !idEmpresaFiltro && (
                              <td style={{ verticalAlign: 'middle', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {empresaNome(grupo.idEmpresa)}
                              </td>
                            )}

                            {/* Contagem de tamanhos */}
                            <td className="text-center" style={{ verticalAlign: 'middle' }}>
                              <span className="badge bg-secondary" style={{ fontSize: 11 }}>
                                {grupo.itens.length} tam.
                              </span>
                            </td>

                            {/* Total disponível */}
                            <td className={`text-end ${dispColor}`} style={{ verticalAlign: 'middle', fontSize: 15 }}>
                              {totalDisp}
                            </td>

                            {/* Total em fabricação */}
                            <td className={`text-end ${totalFab > 0 ? 'text-warning fw-semibold' : 'text-muted'}`}
                              style={{ verticalAlign: 'middle', fontSize: 15 }}>
                              {totalFab > 0 ? totalFab : '—'}
                            </td>
                          </tr>

                          {/* ── Linhas dos tamanhos (expandido) ── */}
                          {aberto && grupo.itens
                            .slice()
                            .sort((a, b) => tamanhoNome(a.id_tamanho_uniforme).localeCompare(tamanhoNome(b.id_tamanho_uniforme), 'pt-BR', { numeric: true }))
                            .map(item => (
                              <tr key={item.id_estoque}
                                style={{ background: '#f8f9fa', borderLeft: '3px solid var(--cjc-accent)' }}>

                                {/* Conector visual */}
                                <td style={{ paddingLeft: 12, color: '#ccc', fontSize: 16, verticalAlign: 'middle' }}>
                                  └
                                </td>

                                {/* Tamanho (+ cor, se houver) */}
                                <td style={{ paddingLeft: 8, verticalAlign: 'middle' }}>
                                  <span className="badge bg-secondary" style={{ fontSize: 12, fontWeight: 600 }}>
                                    {tamanhoNome(item.id_tamanho_uniforme)}
                                  </span>
                                  {item.id_cor && corNome(item.id_cor) && (
                                    <span className="badge bg-light text-dark border ms-1" style={{ fontSize: 11 }}>
                                      {corNome(item.id_cor)}
                                    </span>
                                  )}
                                </td>

                                {emGlobal && !idEmpresaFiltro && <td />}

                                {/* Vazio (coluna tamanhos) */}
                                <td />

                                {/* Disponível */}
                                <td className="text-end" style={{ verticalAlign: 'middle' }}>
                                  <span className={`fw-semibold ${item.quantidade_disponivel === 0 ? 'text-danger' : 'text-success'}`}>
                                    {item.quantidade_disponivel}
                                  </span>
                                </td>

                                {/* Em fabricação */}
                                <td className="text-end" style={{ verticalAlign: 'middle' }}>
                                  <span className={item.quantidade_reservada_fabricacao > 0 ? 'text-warning fw-semibold' : 'text-muted'}>
                                    {item.quantidade_reservada_fabricacao > 0 ? item.quantidade_reservada_fabricacao : '—'}
                                  </span>
                                </td>
                              </tr>
                            ))
                          }

                          {/* Separador sutil entre produtos */}
                          {aberto && (
                            <tr style={{ height: 4, background: 'transparent' }}>
                              <td colSpan={emGlobal && !idEmpresaFiltro ? 6 : 5} style={{ padding: 0, border: 'none' }} />
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <PagEst />
        </>
      )}

      {/* ── ABA: HISTÓRICO DE MOVIMENTOS ── */}
      {aba === 'lancamentos' && (
        <>
          <div className="card">
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-hover mb-0">
                  <thead>
                    <tr>
                      <ThLanc campo="created_at">Data</ThLanc>
                      <ThLanc campo="id_modelo_uniforme">Modelo</ThLanc>
                      {emGlobal && !idEmpresaFiltro && <th>Empresa</th>}
                      <ThLanc campo="id_tamanho_uniforme">Tam.</ThLanc>
                      <th>Cor</th>
                      <th>Tipo</th>
                      <ThLanc campo="quantidade">Qtd.</ThLanc>
                      <th>Observação</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagLanc.length === 0 && (
                      <tr><td colSpan={emGlobal && !idEmpresaFiltro ? 8 : 7} className="text-center text-muted py-4">Nenhum lançamento.</td></tr>
                    )}
                    {pagLanc.map(l => (
                      <tr key={l.id_lancamento}>
                        <td>{formatDateTime(l.created_at)}</td>
                        <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {modeloNome(l.id_modelo_uniforme)}
                        </td>
                        {emGlobal && !idEmpresaFiltro && (
                          <td style={{ maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {empresaNome(modeloMap[l.id_modelo_uniforme]?.id_empresa_operadora)}
                          </td>
                        )}
                        <td><span className="badge bg-secondary">{tamanhoNome(l.id_tamanho_uniforme)}</span></td>
                        <td>{l.id_cor ? (corNome(l.id_cor) || '—') : '—'}</td>
                        <td>
                          <span className={`badge ${l.tipo === 'saida' ? 'bg-danger' : 'bg-success'}`}>
                            {l.tipo === 'saida' ? 'Saída' : 'Entrada'}
                          </span>
                        </td>
                        <td className={`fw-bold ${l.tipo === 'saida' ? 'text-danger' : 'text-success'}`}>
                          {l.tipo === 'saida' ? '−' : '+'}{l.quantidade}
                        </td>
                        <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {l.observacao || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <PagLanc />
        </>
      )}

      {modal && (
        <ModalLancamento modelos={modelos} cores={cores} tamanhosCatalogo={tamanhos} empresas={empresas} emGlobal={emGlobal} initial={prefill} onClose={fecharModal}
          onSaved={() => { fecharModal(); carregar(); }} />
      )}
    </div>
  );
}
